import React from 'react';
import styled, { css } from 'styled-components';
import { HasChildren, EntityTypeName, Entity, PageLayoutComponent } from '@shapeable/types';
import { breakpoints, theme, ColorTokens } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { ContentTab, EntityGrid, SliceLayoutBannerTabs, SliceLayoutEntityImagePageHeader, TabsContainer, UiComponentName, useEntityTypes, usePodcasts, usePosts, useVideos } from '@shapeable/ui';
import { useReflections } from '../../hooks/use-reflections';
const cls = classNames('challenge-layout');

// -------- Types -------->

// -------- Props -------->

// export type PageLayoutEntityTypeGridProps = Classable & HasChildren & { 
//   entity?: Page;
//   maxBodyWidth?: string;
// };

// export const PageLayoutEntityTypeGridDefaultProps: Omit<PageLayoutEntityTypeGridProps, 'entity'> = {
//   maxBodyWidth: '1088px',
// };

// -------- Child Component Props -------->

type ContainerProps = {
  children?: React.ReactNode;
  className?: string;
};

type BoundaryProps = {
  maxWidth?: string;
}

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
  
  `,
});

const BoundaryStyles = breakpoints({
  base: css`
    box-sizing: border-box;
    width: 100%;
    padding: 0 ${theme.UNIT(2)};
    ${({ maxWidth }: BoundaryProps ) => css`
      max-width: ${maxWidth || '100%'};
      margin: 0 auto;
    `}
  `,
});


const TabsStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(2)};
  `,
});

const TabStyles = breakpoints({
  base: css`
    .shp--content-tab__icon-label {
      white-space: nowrap;
    }
  `,
});


const GridStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(4)};
  `,
});





// -------- Components -------->

const My = {
  NAME: 'PageLayoutEntityTypeGrid' as UiComponentName,

  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Tabs: styled(TabsContainer)<ContainerProps>`${TabsStyles}`,
      Tab: styled(ContentTab)`${TabStyles}`,

    Boundary: styled.div<BoundaryProps & HasChildren>`${BoundaryStyles}`,
    
    Grid: styled(EntityGrid)`${GridStyles}`,

  Header: SliceLayoutEntityImagePageHeader,
};

export const PageLayoutEntityTypeGrid: PageLayoutComponent = (props) => {
  // const componentProps = useComponentProps(My.NAME, PageLayoutEntityTypeGridDefaultProps, props) as PageLayoutEntityTypeGridProps;
  const { className, children, entity } = props;

  const reflections = useReflections();
  const posts = usePosts();
  const videos = useVideos();
  const podcasts = usePodcasts();

  const allEntityTypes = useEntityTypes();

  const entityTypes = allEntityTypes.filter((type) => ['Reflection', 'Post', 'Video', 'Podcast'].includes(type.internalName))

  const [ activeEntityType, setActiveEntityType ] = React.useState<EntityTypeName | null>(null);

  // to save time, let's setup a local filter - we can move this to a URL based scheme later
  
  const itemsByType = React.useMemo(() => {
    // filter items by the current challenge
    return {
      Reflection: reflections,
      Post: posts,
      Video: videos,
      Podcast: podcasts,
    };
  }, [entity?.slug]) as Partial<Record<EntityTypeName, Entity[]>>;

  const items: Entity[] = React.useMemo(() => {
    return [
      ...itemsByType.Reflection,
      ...itemsByType.Post,
      ...itemsByType.Video,
      ...itemsByType.Podcast,
    ];
  }, [itemsByType]);

  const activeItems = React.useMemo(() => {
    if (activeEntityType === null) {
      return items;
    }

    return itemsByType[activeEntityType] || [];
  }, [ activeEntityType, items, entity?.slug, itemsByType ]);

  
  return (
    <My.Container className={cls.name(className)}>
      <My.Header entity={entity} />
      <SliceLayoutBannerTabs />

      <My.Boundary maxWidth='1088px'>
      <My.Tabs>
        <My.Tab 
          onClick={() => setActiveEntityType(null)} 
          isActive={activeEntityType === null} 
          isLast={false} 
          label="All Types" 
          count={items.length} 
        />
        {
          entityTypes.map((type) => {
            const count = itemsByType?.[type.internalName as EntityTypeName]?.length || 0;

            if (count === 0) {
              return null; // don't show tabs for types with no items
            }

            return (
              <My.Tab 
                key={type.internalName} 
                wordWrap="nowrap"
                onClick={() => setActiveEntityType(type.internalName as EntityTypeName)} 
                isActive={activeEntityType === type.internalName} 
                isLast={false} 
                label={type.pluralLabel || type.name} 
                count={count} 
              />
            );
          }).filter(Boolean) // filter out nulls
        }
      </My.Tabs>

      <My.Grid 
        spacing={4}
        maxColumns={4}
        anyCardProps={{ showDescription: false }}
        items={activeItems} 
      />
      
      </My.Boundary>

    </My.Container>
  )
};

/* @ts-ignore */
PageLayoutEntityTypeGrid.cls = cls;